import { useCallback, useRef } from "react";
import { TradeToEarnCampaignWithStatsSchema } from "@cede/types";
import { API_ENDPOINT, getCurrentEpoch, getNextEpoch, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useUserSources } from "@/hooks/user/useUserSources";
import { is4xxHttpError } from "@/utils/api/is4xxHttpError";
import { getCampaignWithStatus } from "@/utils/campaign";
import { useQuery } from "@tanstack/react-query";

type CampaignSelectResult = {
  campaign: ReturnType<typeof getCampaignWithStatus> | null;
  currentEpoch: ReturnType<typeof getCurrentEpoch> | null;
  nextEpoch: ReturnType<typeof getNextEpoch> | null;
};

const EMPTY_CAMPAIGN_RESULT: CampaignSelectResult = { campaign: null, currentEpoch: null, nextEpoch: null };

export const useCampaign = (campaignName: string | undefined) => {
  const { getSources } = useUserSources();
  const apiHelper = useApi();
  const { authenticated, auth, isAuthStateStable } = useHubUser();
  const selectCacheRef = useRef<{ raw: unknown; parsed: CampaignSelectResult } | null>(null);

  const selectCampaign = useCallback((data: unknown) => {
    if (!data) {
      return EMPTY_CAMPAIGN_RESULT;
    }

    if (selectCacheRef.current?.raw === data) {
      return selectCacheRef.current.parsed;
    }

    const parsedCampaign = TradeToEarnCampaignWithStatsSchema.safeParse(data);
    if (!parsedCampaign.success) {
      // eslint-disable-next-line no-console
      console.error("Invalid campaign data", parsedCampaign.error);
      return EMPTY_CAMPAIGN_RESULT;
    }

    const campaign = getCampaignWithStatus(parsedCampaign.data);
    const parsed = {
      campaign,
      currentEpoch: getCurrentEpoch(campaign) ?? null,
      nextEpoch: getNextEpoch(campaign) ?? null,
    };

    selectCacheRef.current = { raw: data, parsed };
    return parsed;
  }, []);

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [QueryKeys.CAMPAIGNS_METADATA, campaignName, authenticated],
    queryFn: async () => {
      const sources = getSources();
      if (!campaignName) return null;

      const queryParams = { name: campaignName, utms: sources.join(",") };

      if (authenticated) {
        return apiHelper.getWithAuth(auth, API_ENDPOINT.TRADE_TO_EARN_CAMPAIGN_BY_NAME_AUTHENTICATED, {
          queryParams,
        });
      }

      return apiHelper.getOrThrow(API_ENDPOINT.TRADE_TO_EARN_CAMPAIGN_BY_NAME, {
        queryParams,
      });
    },
    select: selectCampaign,
    enabled: !!campaignName && isAuthStateStable,
    retry: (failureCount, error) => !is4xxHttpError(error) && failureCount < 3,
    refetchInterval: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000,
  });

  return {
    campaign: data?.campaign ?? null,
    currentEpoch: data?.currentEpoch ?? null,
    nextEpoch: data?.nextEpoch ?? null,
    isLoading,
    isFetching,
    isError,
    error,
  };
};
